import { Folder, Trash2 } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AddableList from "../common/AddableList";
import FieldError from "../common/FieldError";

interface FolderEntry {
  folderPath?: string | null;
}

interface FolderListControlProps<T extends FolderEntry> {
  label: string;
  placeholder?: string;
  folders: T[];
  onChange: (folders: T[]) => void;
  errorPathPrefix: string;
  disabled?: boolean;
}

function FolderListControl<T extends FolderEntry>({
  label,
  placeholder = "Folder path",
  folders,
  onChange,
  errorPathPrefix,
  disabled = false,
}: FolderListControlProps<T>) {
  const handleAddClick = () => {
    onChange([...folders, { folderPath: "" } as T]);
  };

  const handleChange = (index: number, value: string) => {
    onChange(
      folders.map((f, i) =>
        i === index ? ({ ...f, folderPath: value.trim() } as T) : f,
      ),
    );
  };

  const removeDuplicates = () => {
    const seen = new Set<string>();
    const unique = folders.filter((f) => {
      const path = (f.folderPath ?? "").trim();
      if (seen.has(path)) return false;
      seen.add(path);
      return true;
    });
    if (unique.length !== folders.length) {
      onChange(unique);
    }
  };

  const handleDelete = (index: number) => {
    onChange(folders.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  return (
    <AddableList
      onAddClick={handleAddClick}
      subheaderText={label}
      disabled={disabled}
    >
      {folders.map((folder, index) => (
        <div key={index} className="flex flex-col gap-1 group">
          <div className="flex items-center gap-2">
            <Folder className="size-4 shrink-0 text-muted-foreground" />
            <Input
              className="h-8 flex-1 min-w-0"
              placeholder={placeholder}
              value={folder.folderPath ?? ""}
              onBlur={removeDuplicates}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={disabled}
            />
            <Button
              type="button"
              aria-label="Delete folder"
              variant="ghost"
              size="icon-sm"
              className="text-muted-foreground hover:text-destructive opacity-60 group-hover:opacity-100 transition-opacity"
              onClick={() => handleDelete(index)}
              disabled={disabled}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
          <FieldError name={`${errorPathPrefix}.${index}.folderPath`} />
        </div>
      ))}
    </AddableList>
  );
}

export default FolderListControl;
